import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  ArrowLeft, 
  Zap, 
  Bot, 
  Code, 
  MessageSquare,
  Settings,
  Eye,
  Copy,
  Upload,
  FileSpreadsheet,
  Download,
  X
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import InputSchemaEditor from './InputSchemaEditor';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { Textarea } from './ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AgentManagement = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState('modules');
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showCreateAgent, setShowCreateAgent] = useState(false);
  const [showEditModule, setShowEditModule] = useState(null);
  const [newAgent, setNewAgent] = useState({
    name: '',
    description: '',
    module_type: '',
    input_schema: '{\n  "query": "string",\n  "parameters": "object"\n}',
    agent_prompt: '',
    agent_type: 'functional',
    related_agents: []
  });
  const queryClient = useQueryClient();

  // Queries
  const { data: modulesRaw = [], refetch: refetchModules, isLoading: modulesLoading, error: modulesError } = useQuery({
    queryKey: ['modules'],
    queryFn: async () => {
      const response = await axios.get(`${API}/modules`);
      const modulesData = response.data;
      
      // Fetch agents for each module
      const modulesWithAgents = await Promise.all(
        modulesData.map(async (module) => {
          try {
            const agentsResponse = await axios.get(`${API}/modules/${module.type}/agents`);
            return { ...module, agents: agentsResponse.data };
          } catch (error) {
            console.error(`Error fetching agents for ${module.type}:`, error);
            toast.error(`Error al cargar agentes para ${module.name}`);
            return { ...module, agents: [] };
          }
        })
      );
      
      return modulesWithAgents;
    },
    retry: 2,
    staleTime: 0, // Always fetch fresh data
    cacheTime: 0, // Don't cache
    refetchOnMount: true, // Always refetch on mount
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    keepPreviousData: false, // Don't keep old data
    onError: (error) => {
      console.error('Error loading modules and agents:', error);
      toast.error(`Error al cargar módulos: ${error.response?.data?.detail || error.message}`);
    }
  });

  // Sort modules in specific order
  const modules = React.useMemo(() => {
    const moduleOrder = {
      'revenue': 1,
      'supply_chain': 2,
      'logistica': 3,
      'logistics': 3,
      'operaciones': 4,
      'operations': 4,
      'finanzas': 5,
      'finance': 5,
      'rrhh': 6,
      'human_resources': 6,
    };
    
    return [...modulesRaw].sort((a, b) => {
      const orderA = moduleOrder[a.type?.toLowerCase()] || 999;
      const orderB = moduleOrder[b.type?.toLowerCase()] || 999;
      return orderA - orderB;
    });
  }, [modulesRaw]);

  // Derive allAgents from modules data
  const allAgents = React.useMemo(() => {
    return modules.flatMap(module => module.agents || []);
  }, [modules]);

  // Mutations
  const updateModuleMutation = useMutation({
    mutationFn: async ({ moduleId, masterPrompt, defaultModel }) => {
      const response = await axios.put(`${API}/modules/${moduleId}`, {
        master_prompt: masterPrompt,
        default_model: defaultModel
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['modules']);
      setShowEditModule(null);
      toast.success('Módulo actualizado exitosamente');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al actualizar módulo');
    }
  });

  const createAgentMutation = useMutation({
    mutationFn: async (agentData) => {
      let inputSchema;
      try {
        inputSchema = JSON.parse(agentData.input_schema);
      } catch {
        throw new Error('El schema de entrada debe ser JSON válido');
      }
      
      const response = await axios.post(`${API}/agents`, {
        ...agentData,
        input_schema: inputSchema
      });
      
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['modules']);
      setShowCreateAgent(false);
      setNewAgent({
        name: '',
        description: '',
        module_type: '',
        input_schema: '{\n  "query": "string",\n  "parameters": "object"\n}',
        agent_prompt: '',
        agent_type: 'functional',
        related_agents: []
      });
      toast.success('Agente creado exitosamente');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al crear agente');
    }
  });

  const updateAgentMutation = useMutation({
    mutationFn: async ({ agentId, agentData }) => {
      let inputSchema;
      try {
        inputSchema = typeof agentData.input_schema === 'string' 
          ? JSON.parse(agentData.input_schema)
          : agentData.input_schema;
      } catch {
        throw new Error('El schema de entrada debe ser JSON válido');
      }

      const response = await axios.put(`${API}/agents/${agentId}`, {
        ...agentData,
        input_schema: inputSchema
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['modules']);
      setSelectedAgent(null);
      toast.success('Agente actualizado exitosamente');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al actualizar agente');
    }
  });

  const deleteAgentMutation = useMutation({
    mutationFn: async (agentId) => {
      const response = await axios.delete(`${API}/agents/${agentId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['modules']);
      toast.success('Agente eliminado exitosamente');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al eliminar agente');
    }
  });

  const handleCreateAgent = () => {
    if (!newAgent.name || !newAgent.description || !newAgent.module_type || !newAgent.agent_prompt) {
      toast.error('Complete todos los campos requeridos');
      return;
    }
    createAgentMutation.mutate(newAgent);
  };

  const handleUpdateAgent = (agentData) => {
    updateAgentMutation.mutate({ 
      agentId: selectedAgent.id, 
      agentData 
    });
  };

  const handleUpdateModule = (moduleData) => {
    updateModuleMutation.mutate({
      moduleId: showEditModule.id,
      masterPrompt: moduleData.master_prompt,
      defaultModel: moduleData.default_model
    });
  };

  // Group and sort agents by their parent module
  const agentsByModule = React.useMemo(() => {
    const grouped = {};
    
    // Define sort order for agent types
    const typeOrder = {
      'functional': 1,
      'premium': 2,
      'c_level': 3,
      'c-level': 3, // Handle both formats
    };
    
    // Helper function to extract agent number from name
    const extractAgentNumber = (name) => {
      // Match patterns like "FUNC-RVM-002", "PREM-RVM-001", "CLVL-RVM-001", or any 3-digit number
      const match = name?.match(/(\d{3})/);
      return match ? parseInt(match[1], 10) : 999;
    };
    
    modules.forEach(module => {
      const agents = module.agents || [];
      
      // Sort agents: first by type (functional, premium, c-level), then by number ascending
      const sortedAgents = [...agents].sort((a, b) => {
        // First, sort by agent type
        const typeA = typeOrder[a.agent_type?.toLowerCase()] || 999;
        const typeB = typeOrder[b.agent_type?.toLowerCase()] || 999;
        
        if (typeA !== typeB) {
          return typeA - typeB;
        }
        
        // If same type, sort by agent number (ascending)
        const numA = extractAgentNumber(a.name);
        const numB = extractAgentNumber(b.name);
        return numA - numB;
      });
      
      grouped[module.type] = sortedAgents;
    });
    
    return grouped;
  }, [modules]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          <Button
            onClick={onBack}
            variant="ghost"
            size="sm"
            className="text-slate-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al Dashboard
          </Button>
          <div className="h-6 w-px bg-slate-600" />
          <div>
            <h1 className="text-3xl font-bold text-white">Gestión de Agentes y Módulos</h1>
            <p className="text-slate-400">Configure prompts maestros y agentes globales del sistema</p>
          </div>
        </div>
      </div>

      {/* Configuration Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800 border-slate-600">
          <TabsTrigger value="modules" className="data-[state=active]:bg-blue-600">
            <Zap className="w-4 h-4 mr-2" />
            Configuración de Módulos
          </TabsTrigger>
          <TabsTrigger value="agents" className="data-[state=active]:bg-blue-600">
            <Bot className="w-4 h-4 mr-2" />
            Catálogo Global de Agentes
          </TabsTrigger>
        </TabsList>

        {/* Modules Configuration */}
        <TabsContent value="modules" className="space-y-6">
          <Card className="glass-dark border-blue-500/20">
            <CardHeader>
              <CardTitle className="text-white">Prompt Maestro por Módulo</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {modules.map((module) => (
                  <div key={module.id} className="p-6 rounded-lg bg-slate-800/50 border border-slate-700">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="font-semibold text-white mb-1">{module.name}</h3>
                        <p className="text-sm text-slate-400">{module.description}</p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => setShowEditModule(module)}
                        className="btn-gradient"
                        data-testid={`edit-module-${module.type}`}
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Editar
                      </Button>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-400 mb-1">
                          Modelo por Defecto
                        </label>
                        <Badge className="badge-info text-xs">
                          {module.default_model}
                        </Badge>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-slate-400 mb-1">
                          Agentes Disponibles
                        </label>
                        <span className="text-sm text-slate-300">
                          {agentsByModule[module.type]?.length || 0} agentes
                        </span>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-slate-400 mb-1">
                          Prompt Maestro (Vista Previa)
                        </label>
                        <div className="text-xs text-slate-300 bg-slate-700 p-2 rounded text-ellipsis overflow-hidden max-h-16">
                          {module.master_prompt}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Agents Management */}
        <TabsContent value="agents" className="space-y-6">
          <Card className="glass-dark border-blue-500/20">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-white">Catálogo Global de Agentes</CardTitle>
                <Button 
                  className="btn-gradient"
                  onClick={() => setShowCreateAgent(true)}
                  data-testid="create-agent-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Crear Agente
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {modules.map((module) => (
                <div key={module.type} className="mb-8">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-blue-400" />
                    {module.name}
                    <Badge className="badge-info ml-2">
                      {agentsByModule[module.type]?.length || 0} agentes
                    </Badge>
                  </h3>
                  
                  <div className="space-y-4">
                    {/* Group agents by type with visual separators */}
                    {['functional', 'premium', 'c_level'].map((agentType) => {
                      const typeAgents = agentsByModule[module.type]?.filter(a => a.agent_type === agentType) || [];
                      if (typeAgents.length === 0) return null;
                      
                      const typeLabel = agentType === 'c_level' ? 'C-Level' : 
                                       agentType === 'premium' ? 'Premium' : 'Funcional';
                      const typeColor = agentType === 'c_level' ? 'text-purple-400 border-purple-500/30' :
                                       agentType === 'premium' ? 'text-amber-400 border-amber-500/30' :
                                       'text-blue-400 border-blue-500/30';
                      
                      return (
                        <div key={agentType}>
                          <div className={`flex items-center gap-2 mb-3 pb-2 border-b ${typeColor}`}>
                            <span className="text-xs font-semibold uppercase tracking-wider">{typeLabel}</span>
                            <span className="text-xs opacity-60">({typeAgents.length})</span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                            {typeAgents.map((agent) => (
                              <div key={agent.id} className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-medium text-white text-sm">{agent.name}</h4>
                                    <Badge
                                      className={
                                        agent.agent_type === 'c_level' ? 'bg-purple-500/20 text-purple-300 border-purple-500/30' :
                                        agent.agent_type === 'premium' ? 'bg-amber-500/20 text-amber-300 border-amber-500/30' :
                                        'bg-blue-500/20 text-blue-300 border-blue-500/30'
                                      }
                                      style={{ fontSize: '10px', padding: '2px 6px' }}
                                    >
                                      {agent.agent_type === 'c_level' ? 'C-Level' :
                                       agent.agent_type === 'premium' ? 'Premium' :
                                       'Funcional'}
                                    </Badge>
                                  </div>
                                  <div className="flex gap-1">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => setSelectedAgent(agent)}
                                      className="h-6 w-6 p-0 text-blue-400 hover:text-blue-300"
                                      data-testid={`edit-agent-${agent.id}`}
                                    >
                                      <Edit className="w-3 h-3" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        if (confirm('¿Está seguro de eliminar este agente?')) {
                                          deleteAgentMutation.mutate(agent.id);
                                        }
                                      }}
                                      className="h-6 w-6 p-0 text-red-400 hover:text-red-300"
                                      data-testid={`delete-agent-${agent.id}`}
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </div>
                                
                                <p className="text-xs text-slate-400 mb-3 line-clamp-2">{agent.description}</p>
                                
                                <div className="space-y-1 text-xs">
                                  <div className="flex justify-between text-slate-500">
                                    <span>Estado:</span>
                                    <Badge className={agent.is_active ? 'badge-success' : 'badge-error'}>
                                      {agent.is_active ? 'Activo' : 'Inactivo'}
                                    </Badge>
                                  </div>
                                  {agent.related_agents && agent.related_agents.length > 0 && (
                                    <div className="flex justify-between text-slate-500">
                                      <span>Relacionados:</span>
                                      <span className="text-blue-400">
                                        {/* Count only valid related agents that exist in the same module */}
                                        {agent.related_agents.filter(raId => 
                                          modules.find(m => m.type === agent.module_type)?.agents?.some(a => a.id === raId)
                                        ).length}
                                      </span>
                                    </div>
                                  )}
                                  <div className="flex justify-between text-slate-500">
                                    <span>Creado:</span>
                                    <span>{new Date(agent.created_at).toLocaleDateString()}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Module Dialog */}
      <Dialog open={!!showEditModule} onOpenChange={() => setShowEditModule(null)}>
        <DialogContent className="glass-dark border-blue-500/20 text-white max-w-4xl">
          <DialogHeader>
            <DialogTitle>Configurar Módulo: {showEditModule?.name}</DialogTitle>
          </DialogHeader>
          {showEditModule && (
            <ModuleEditForm 
              module={showEditModule}
              onSave={handleUpdateModule}
              onCancel={() => setShowEditModule(null)}
              isLoading={updateModuleMutation.isPending}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Create Agent Dialog */}
      <Dialog open={showCreateAgent} onOpenChange={setShowCreateAgent}>
        <DialogContent className="glass-dark border-blue-500/20 text-white max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Crear Nuevo Agente</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 pr-2">
          <AgentForm
            agent={newAgent}
            modules={modules}
            onChange={setNewAgent}
            onSave={handleCreateAgent}
            onCancel={() => setShowCreateAgent(false)}
            isLoading={createAgentMutation.isPending}
            isCreate={true}
          />
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Agent Dialog */}
      <Dialog open={!!selectedAgent} onOpenChange={() => setSelectedAgent(null)}>
        <DialogContent className="glass-dark border-blue-500/20 text-white max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Editar Agente: {selectedAgent?.name}</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 pr-2">
          {selectedAgent && (
            <AgentForm
              agent={{
                ...selectedAgent,
                input_schema: typeof selectedAgent.input_schema === 'string' 
                  ? selectedAgent.input_schema 
                  : JSON.stringify(selectedAgent.input_schema, null, 2)
              }}
              modules={modules}
              onChange={() => {}}
              onSave={handleUpdateAgent}
              onCancel={() => setSelectedAgent(null)}
              isLoading={updateAgentMutation.isPending}
              isCreate={false}
            />
          )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Module Edit Form Component
const ModuleEditForm = ({ module, onSave, onCancel, isLoading }) => {
  const [formData, setFormData] = useState({
    name: module.name,
    master_prompt: module.master_prompt,
    default_model: module.default_model,
    temperature: module.temperature || 0.7,
    max_tokens: module.max_tokens || 4096
  });

  const handleSave = () => {
    if (!formData.master_prompt.trim()) {
      toast.error('El prompt maestro es requerido');
      return;
    }
    if (formData.temperature < 0 || formData.temperature > 2) {
      toast.error('La temperatura debe estar entre 0 y 2');
      return;
    }
    if (formData.max_tokens < 100 || formData.max_tokens > 100000) {
      toast.error('Los tokens deben estar entre 100 y 100,000');
      return;
    }
    onSave(formData);
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">
          Nombre del Módulo
        </label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="form-input"
          placeholder="Nombre personalizado del módulo"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">
          Modelo por Defecto
        </label>
        <Select
          value={formData.default_model}
          onValueChange={(value) => setFormData({ ...formData, default_model: value })}
        >
          <SelectTrigger className="form-input">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="gpt-5">🤖 GPT-5 (Más reciente - OpenAI)</SelectItem>
            <SelectItem value="gpt-4o">🤖 GPT-4o (Balanceado)</SelectItem>
            <SelectItem value="gpt-4o-mini">🤖 GPT-4o Mini (Rápido y Económico)</SelectItem>
            <SelectItem value="gpt-4.1">🤖 GPT-4.1 (Máxima Precisión)</SelectItem>
            <SelectItem value="claude-opus-4.1">🧠 Claude Opus 4.1 (Máxima Capacidad)</SelectItem>
            <SelectItem value="claude-sonnet-4.5">🧠 Claude Sonnet 4.5 (Balanceado)</SelectItem>
            <SelectItem value="claude-haiku-4.5">🧠 Claude Haiku 4.5 (Rápido y Económico)</SelectItem>
            <SelectItem value="claude-3-5-sonnet-20241022">🧠 Claude 3.5 Sonnet (Razonamiento Superior)</SelectItem>
            <SelectItem value="claude-3-haiku-20240307">🧠 Claude 3 Haiku (Rápido)</SelectItem>
            <SelectItem value="claude-3-opus-20240229">🧠 Claude 3 Opus (Máxima Capacidad v3)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">
          Temperatura de IA: {formData.temperature}
        </label>
        <div className="flex items-center gap-4">
          <span className="text-xs text-slate-400">0 (Preciso)</span>
          <input
            type="range"
            min="0"
            max="2"
            step="0.1"
            value={formData.temperature}
            onChange={(e) => setFormData({ ...formData, temperature: parseFloat(e.target.value) })}
            className="flex-1 h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer slider-thumb"
          />
          <span className="text-xs text-slate-400">2 (Creativo)</span>
        </div>
        <p className="text-xs text-slate-400 mt-1">
          Controla la creatividad de las respuestas. 0 = más determinista, 2 = más creativo.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">
          Máxima Capacidad de Tokens
        </label>
        <Input
          type="number"
          value={formData.max_tokens}
          onChange={(e) => setFormData({ ...formData, max_tokens: parseInt(e.target.value) || 4096 })}
          className="form-input"
          min="100"
          max="100000"
          step="100"
        />
        <p className="text-xs text-slate-400 mt-1">
          Número máximo de tokens que puede procesar en la entrada (100 - 100,000).
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">
          Prompt Maestro del Módulo (Orquestador)
        </label>
        <Textarea
          value={formData.master_prompt}
          onChange={(e) => setFormData({ ...formData, master_prompt: e.target.value })}
          className="form-input min-h-[200px] font-mono text-sm"
          placeholder="Eres el coordinador del módulo de [MÓDULO]. Tu objetivo es orquestar..."
          data-testid="module-master-prompt"
        />
        <p className="text-xs text-slate-400 mt-1">
          Este prompt orquesta e integra todos los agentes del módulo (Funcionales, Premium y C-Level). Se ejecuta antes que el prompt específico de cada agente.
        </p>
      </div>

      <div className="flex gap-3">
        <Button
          onClick={handleSave}
          className="flex-1 btn-gradient"
          disabled={isLoading}
          data-testid="save-module-btn"
        >
          {isLoading ? 'Guardando...' : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Guardar Configuración
            </>
          )}
        </Button>
        <Button
          onClick={onCancel}
          variant="outline"
          className="border-slate-600 text-slate-300"
        >
          Cancelar
        </Button>
      </div>
    </div>
  );
};

// Agent Form Component  
const AgentForm = ({ agent, modules, onChange, onSave, onCancel, isLoading, isCreate }) => {
  const [formData, setFormData] = useState(agent);

  // Update formData when agent prop changes (important for edit mode)
  React.useEffect(() => {
    console.log('[AgentForm] useEffect - agent prop changed:', agent, 'input_schema type:', typeof agent?.input_schema);
    setFormData(agent);
  }, [agent]);

  const handleSave = () => {
    if (!formData.name || !formData.description || !formData.module_type || !formData.agent_prompt) {
      toast.error('Complete todos los campos requeridos');
      return;
    }
    onSave(formData);
  };

  const updateField = (field, value) => {
    const updated = { ...formData, [field]: value };
    setFormData(updated);
    if (isCreate) onChange(updated);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1.5">
            Nombre del Agente *
          </label>
          <Input
            value={formData.name}
            onChange={(e) => updateField('name', e.target.value)}
            className="form-input"
            placeholder="LeadCollector"
            data-testid="agent-name-input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1.5">
            Módulo *
          </label>
          <Select
            value={formData.module_type}
            onValueChange={(value) => updateField('module_type', value)}
            disabled={!isCreate}
          >
            <SelectTrigger className="form-input">
              <SelectValue placeholder="Seleccione módulo" />
            </SelectTrigger>
            <SelectContent>
              {modules.map(module => (
                <SelectItem key={module.type} value={module.type}>
                  {module.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-300 mb-1.5">
          Descripción *
        </label>
        <Textarea
          value={formData.description}
          onChange={(e) => updateField('description', e.target.value)}
          className="form-input min-h-[60px]"
          placeholder="Recopila y analiza leads de múltiples fuentes..."
          data-testid="agent-description"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1.5">
            Tipo de Agente *
          </label>
          <Select
            value={formData.agent_type || 'functional'}
            onValueChange={(value) => updateField('agent_type', value)}
          >
            <SelectTrigger className="form-input">
              <SelectValue placeholder="Seleccione tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="functional">
                <div className="flex flex-col items-start">
                  <span className="font-medium">Funcional</span>
                  <span className="text-xs text-slate-400">Ejecuta procesos y realiza cálculos</span>
                </div>
              </SelectItem>
              <SelectItem value="premium">
                <div className="flex flex-col items-start">
                  <span className="font-medium">Premium</span>
                  <span className="text-xs text-slate-400">Genera predicciones basadas en datos</span>
                </div>
              </SelectItem>
              <SelectItem value="c_level">
                <div className="flex flex-col items-start">
                  <span className="font-medium">C-Level</span>
                  <span className="text-xs text-slate-400">Genera proyecciones estratégicas ejecutivas</span>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1.5">
            Agentes Relacionados (mismo módulo)
          </label>
          <Select
            value=""
            onValueChange={(agentId) => {
              if (agentId && !formData.related_agents.includes(agentId)) {
                updateField('related_agents', [...(formData.related_agents || []), agentId]);
              }
            }}
            disabled={!formData.module_type}
          >
            <SelectTrigger className="form-input">
              <SelectValue placeholder="Agregar agente relacionado" />
            </SelectTrigger>
            <SelectContent>
              {modules
                .find(m => m.type === formData.module_type)
                ?.agents
                ?.filter(a => a.id !== formData.id && !(formData.related_agents || []).includes(a.id))
                .map(agent => (
                  <SelectItem key={agent.id} value={agent.id}>
                    {agent.name}
                  </SelectItem>
                )) || []}
            </SelectContent>
          </Select>
          {formData.related_agents && formData.related_agents.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {formData.related_agents.map(agentId => {
                const agent = modules
                  .find(m => m.type === formData.module_type)
                  ?.agents?.find(a => a.id === agentId);
                return agent ? (
                  <Badge key={agentId} variant="secondary" className="flex items-center gap-1">
                    {agent.name}
                    <X
                      className="w-3 h-3 cursor-pointer hover:text-red-400"
                      onClick={() => {
                        updateField('related_agents', formData.related_agents.filter(id => id !== agentId));
                      }}
                    />
                  </Badge>
                ) : (
                  <Badge key={agentId} variant="destructive" className="flex items-center gap-1 opacity-60">
                    ID inválido
                    <X
                      className="w-3 h-3 cursor-pointer hover:text-white"
                      onClick={() => {
                        updateField('related_agents', formData.related_agents.filter(id => id !== agentId));
                      }}
                    />
                  </Badge>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Input Schema - Different for Functional vs Premium/C-Level */}
      {formData.agent_type === 'functional' ? (
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Configuración de Input Schema *
          </label>
          <p className="text-xs text-slate-400 mb-3">
            Define qué campos de datos necesita este agente funcional. Usa IA para generar automáticamente, selecciona un template predefinido, o configura manualmente.
          </p>
          {(() => {
            console.log('[AgentForm] Passing to InputSchemaEditor - agent:', formData, 'input_schema:', formData.input_schema, 'type:', typeof formData.input_schema);
            return null;
          })()}
          <InputSchemaEditor 
            agent={formData}
            userToken={localStorage.getItem('token')}
            isCreationMode={isCreate}
            onSchemaChange={(fields) => {
              // Update the input_schema in formData
              updateField('input_schema', JSON.stringify({ fields }, null, 2));
            }}
            onSchemaUpdate={() => {
              // Schema is updated, refresh if needed
            }}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">
              Schema de Entrada (JSON) *
            </label>
            <Textarea
              value={formData.input_schema}
              onChange={(e) => updateField('input_schema', e.target.value)}
              className="form-input min-h-[100px] font-mono text-xs"
              placeholder='{"query": "string", "parameters": "object"}'
              data-testid="agent-input-schema"
            />
            <p className="text-xs text-slate-400 mt-1">
              Estructura JSON esperada
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">
              Prompt del Agente *
            </label>
            <Textarea
              value={formData.agent_prompt}
              onChange={(e) => updateField('agent_prompt', e.target.value)}
              className="form-input min-h-[100px] font-mono text-xs"
              placeholder="Eres un experto en análisis de leads..."
              data-testid="agent-prompt"
            />
            <p className="text-xs text-slate-400 mt-1">
              Define el comportamiento del agente
            </p>
          </div>
        </div>
      )}

      {/* Prompt for Functional agents (full width) */}
      {formData.agent_type === 'functional' && (
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1.5">
            Prompt del Agente *
          </label>
          <Textarea
            value={formData.agent_prompt}
            onChange={(e) => updateField('agent_prompt', e.target.value)}
            className="form-input min-h-[100px] font-mono text-xs"
            placeholder="Eres un experto en análisis de leads..."
            data-testid="agent-prompt"
          />
          <p className="text-xs text-slate-400 mt-1">
            Define el comportamiento del agente
          </p>
        </div>
      )}

      {isCreate && formData.agent_type !== 'functional' && (
        <div className="border border-blue-500/30 rounded-lg p-4 bg-blue-500/10">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
              <Bot className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-blue-300 mb-1">
                {formData.agent_type === 'premium' ? 'Agente Premium - Predicciones' : 'Agente C-Level - Proyecciones'}
              </h4>
              <p className="text-xs text-slate-300">
                {formData.agent_type === 'premium' 
                  ? 'Este agente NO requiere plantilla Excel. Genera predicciones basadas en los datos procesados y calculados por los agentes funcionales del módulo.'
                  : 'Este agente NO requiere plantilla Excel. Genera proyecciones estratégicas ejecutivas (CEO, CFO, COO, etc.) basadas en datos procesados y predicciones de otros agentes del módulo.'
                }
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-3 pt-2 sticky bottom-0 bg-slate-900/95 backdrop-blur-sm pb-2 -mb-2">
        <Button
          onClick={handleSave}
          className="flex-1 btn-gradient"
          disabled={isLoading}
          data-testid="save-agent-btn"
        >
          {isLoading ? 'Guardando...' : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {isCreate ? 'Crear Agente' : 'Actualizar Agente'}
            </>
          )}
        </Button>
        <Button
          onClick={onCancel}
          variant="outline"
          className="border-slate-600 text-slate-300"
        >
          Cancelar
        </Button>
      </div>
    </div>
  );
};

export default AgentManagement;